import React, { useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Input,
  InputProps,
  Label,
  mergeClasses,
  useId,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';

import { useBudgetInputStyles } from './styles/budget-input-styles';

declare function _TL_(arg0: string): string;

interface BudgetInputPureProps {
  className?: string;
  value?: string;
  onChange: (value: string) => void;
  currency: string;
  label?: string;
  helpTopic?: string;
  helpLabel?: string;
  showDropdown?: boolean;
  onSelectCurrency?: (event: any, data: any) => void;
  currencies?: { code: string; text: string }[];
}

export const BudgetInputPure: React.FC<BudgetInputPureProps> = (props) => {
  const {
    className,
    value,
    onChange,
    currency,
    label,
    helpTopic,
    helpLabel,
    showDropdown,
    onSelectCurrency,
    currencies = [],
  } = props;
  const id = useId('budget-input');
  const classes = useBudgetInputStyles();
  const i18n = useContext(I18nContext) as any;

  const onInputChange: InputProps['onChange'] = useCallback(
    (_e, data) => {
      onChange(data?.value);
    },
    [onChange]
  );

  const selectedOption = useMemo(() => {
    if (!showDropdown || !currency) {
      return { code: '', text: '' };
    }
    return (
      _.find(currencies, c => c.code === currency) || { code: '', text: '' }
    );
  }, [showDropdown, currency, currencies]);

  return (
    <div className={mergeClasses('budget-input-container', className)}>
      <Label htmlFor={id} className={classes.label}>
        <span className="budget-label-text">
          {label || i18n.getString(_TL_('Daily budget'))}
        </span>
        {helpTopic && (
          <HelpPopupInline
            aria-label={
              i18n.getString(_TL_('Click to learn more about budget')) ||
              helpLabel
            }
            topic={helpTopic}
          />
        )}
      </Label>
      <div className={mergeClasses('budget-input-row', classes.row)}>
        <Input
          id={id}
          className={mergeClasses('budget-input', classes.input)}
          value={value}
          onChange={onInputChange}
        />
        {showDropdown ? (
          <Dropdown
            className={mergeClasses('currency-dropdown', classes.dropdown)}
            placeholder={i18n.getString(_TL_('Select currency'))}
            onOptionSelect={onSelectCurrency}
            selectedOptions={[selectedOption.code || '']}
            value={selectedOption.text}
            id={`selected-currency-${selectedOption.code}`}
          >
            {_.map(currencies, c => (
              <Option key={c.code} value={c.code} id={`option-${c.code}`}>
                {c.text}
              </Option>
            ))}
          </Dropdown>
        ) : (
          <span className={mergeClasses('budget-currency', classes.currency)}>
            {currency}
          </span>
        )}
      </div>
    </div>
  );
};

BudgetInputPure.defaultProps = {
  className: '',
  value: undefined,
  label: undefined,
  helpTopic: undefined,
  helpLabel: undefined,
  showDropdown: false,
  onSelectCurrency: _.noop,
  currencies: [],
};
